import { createContext, type ReactNode, useContext } from "react";

export interface ActiveDrawerRowContextValue {
	rowId: string | null;
}

const ActiveDrawerRowContext =
	createContext<ActiveDrawerRowContextValue | null>(null);

export interface ActiveDrawerRowRootProps {
	/** Entity id of the open drawer row, or `null` when no drawer is open. */
	rowId: string | null;
	children: ReactNode;
}

/**
 * Lifts the active drawer row id for descendant table composition.
 */
export function ActiveDrawerRowRoot({
	rowId,
	children,
}: ActiveDrawerRowRootProps) {
	return (
		<ActiveDrawerRowContext.Provider value={{ rowId }}>
			{children}
		</ActiveDrawerRowContext.Provider>
	);
}

/**
 * Reads the active drawer row context.
 *
 * @throws When used outside {@link ActiveDrawerRowRoot}.
 */
export function useActiveDrawerRow(): ActiveDrawerRowContextValue {
	const context = useContext(ActiveDrawerRowContext);
	if (!context) {
		throw new Error(
			"useActiveDrawerRow must be used within ActiveDrawerRow.Root",
		);
	}
	return context;
}

/**
 * Optional context read for internals that may render outside a Root.
 */
export function useOptionalActiveDrawerRow(): ActiveDrawerRowContextValue | null {
	return useContext(ActiveDrawerRowContext);
}
